/**
 * web/src/screens/Notebook/giftflow/step3.tsx — Step 3, Ideas: the editable
 * brief strip (Edit opens the Brief; edits regenerate), the three idea cards
 * each with a source label, a Why line, a deadline line and Pick / Save /
 * Not this, and the footnote (card NYJEJRCBfBGQ).
 *
 * apiserpent credits are 0, so every rec the fixture path writes is
 * source 'idea' and renders "Idea, AI-suggested." — the "Live listing"
 * label exists for the day listings can be fetched, and a fixture is never
 * labelled as one.
 */
import { useState } from 'react';
import type { GiftFlowState } from './hooks';
import { REJECT_REASONS, budgetLabel, fmtDeadlineLine, kindLabel } from './types';
import type { GiftSearchRec } from './types';

function priceLine(rec: GiftSearchRec): string {
  if (rec.price !== null) return `A$${rec.price}`;
  if (rec.priceEstimate) return `~${rec.priceEstimate}`;
  return '';
}

export function StepIdeas({ g }: { g: GiftFlowState }) {
  const [rejecting, setRejecting] = useState<string | null>(null);
  const partner = g.partnerName;
  const brief = g.brief;
  const p = brief?.parsed;

  // The three newest non-rejected recs.
  const shown = [...g.recs].filter((r) => r.state !== 'rejected').slice(-3).reverse();

  return (
    <section className="gf-step" data-testid="gift-step-3">
      {/* Editable brief strip — summarises every brief field; Edit opens the
          Brief (step 2) and edits regenerate. */}
      <div className="gf-brief-strip" data-testid="gift-brief-strip">
        <div className="gf-brief-fields">
          <span>{g.active?.occasionType ?? ''}</span>
          {p?.kind && <span>Kind: {kindLabel(p.kind)}</span>}
          <span>Budget: {budgetLabel(p?.budgetMin ?? null, p?.budgetMax ?? null)}</span>
          {p?.vibe && <span>Vibe: {p.vibe}</span>}
          {p && p.avoid.length > 0 && (
            <span data-testid="gift-brief-avoid">Avoid: {p.avoid.join(', ')}</span>
          )}
          {brief?.freeText && (
            <span className="gf-brief-freetext" data-testid="gift-brief-freetext">&ldquo;{brief.freeText}&rdquo;</span>
          )}
        </div>
        <button
          type="button"
          className="pear-btn pear-btn-quiet gf-brief-edit"
          data-testid="gift-brief-edit"
          onClick={() => g.setView(2)}
        >
          Edit
        </button>
      </div>

      <h4 className="gf-heading" data-testid="gift-ideas-heading">
        {shown.length === 0 && g.ideaNoFit ? `Nothing fits ${partner} yet` : `${shown.length} ideas for ${partner}`}
      </h4>

      {shown.length === 0 && !g.ideaNoFit && (
        <p className="gf-muted" data-testid="gift-ideas-empty">
          No ideas yet — go back to the questions and tap Find ideas.
        </p>
      )}

      {/* The floor found nothing worth showing: say so, ask ONE follow-up, and
          offer the way back to the brief. Never three weak cards (card
          t23OZwdtxs4k, spec §7). */}
      {shown.length === 0 && g.ideaNoFit && (
        <div className="gf-no-fit" data-testid="gift-ideas-no-fit">
          <p className="gf-nofit-line" data-testid="gift-ideas-no-fit-copy">
            Nothing fits that brief yet, so there is nothing worth showing you.
          </p>
          <p className="gf-nofit-question" data-testid="gift-ideas-follow-up">
            {g.ideaNoFit.followUp}
          </p>
          <button
            type="button"
            className="pear-btn pear-btn-mustard"
            data-testid="gift-ideas-no-fit-edit"
            onClick={() => g.setView(2)}
          >
            Add that to the brief
          </button>
        </div>
      )}

      <ul className="gf-ideas">
        {shown.map((rec) => (
          <li key={rec.recId} className="gf-idea" data-testid={`gift-idea-${rec.recId}`}>
            <span className="gf-idea-title" data-testid="gift-idea-title">{rec.title}</span>
            <span className="gf-idea-price" data-testid="gift-idea-price">{priceLine(rec)}</span>
            <span
              className={`gf-idea-source${rec.source === 'idea' ? ' is-idea' : ''}`}
              data-testid="gift-idea-source"
            >
              {rec.source === 'listing' ? 'Live listing' : 'Idea, AI-suggested.'}
            </span>
            <span className="gf-idea-why" data-testid="gift-idea-why">{rec.rationale}</span>
            {rec.deadline && (
              <span className="gf-idea-deadline" data-testid="gift-idea-deadline">
                {fmtDeadlineLine(rec.deadline)}
              </span>
            )}
            <div className="gf-idea-actions">
              <button
                type="button"
                className="pear-btn pear-btn-mustard"
                data-testid="gift-idea-pick"
                disabled={g.busy}
                onClick={() => void g.pickRec(rec)}
              >
                Pick
              </button>
              <button
                type="button"
                className="pear-btn pear-btn-quiet"
                data-testid="gift-idea-save"
                disabled={g.busy || rec.state === 'saved'}
                onClick={() => void g.saveRec(rec)}
              >
                {rec.state === 'saved' ? 'Saved' : 'Save'}
              </button>
              <button
                type="button"
                className="pear-btn pear-btn-quiet"
                data-testid="gift-idea-not-this"
                disabled={g.busy}
                onClick={() => setRejecting(rejecting === rec.recId ? null : rec.recId)}
              >
                Not this
              </button>
            </div>
            {rejecting === rec.recId && (
              <div className="gf-reject-reasons" data-testid="gift-reject-reasons">
                {REJECT_REASONS.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    className="gf-chip"
                    data-testid={`gift-reject-${r.value}`}
                    disabled={g.busy}
                    onClick={() => void g.rejectRec(rec, r)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>

      <p className="gf-muted gf-footnote" data-testid="gift-step3-footnote">
        Listings come from product search. Ideas fill gaps where nothing fits. Not this asks why and updates your brief.
      </p>
    </section>
  );
}
